import React, { useEffect, useState } from 'react'
import { Select } from '../../../ui'
import { useStore } from '../../../StateProvider'
import { equals, isNil, map } from 'ramda'
import { useTranslation } from 'react-i18next'
import { fetchEmploymentPositions } from '../../api'
import { usePrevious } from '../../utils/hooks'
import { getUserValue } from '../utils'
import { EMPLOYMENT_ID } from '../../../constants/constants'

const ExternalEmploymentPosition = ({ label, name, additionalInfo, showLabelAsPlaceholder }) => {
  const { t, i18n } = useTranslation()
  const [value, setValue] = useState('')
  const [fldLabel, setFldLabel] = useState(t(`generatedField.${label}`))
  const [fetchError, setFetchError] = useState('')
  const [list, setList] = useState([])
  const [fullList, setFullList] = useState([])
  const [isValid, setIsValid] = useState(null)
  const [isValuePreset, setIsValuePreset] = useState(false)
  const [{ form, form: { accId }, user }, dispatch] = useStore()
  const { businessTypeId, employmentSituationId } = form[accId]
  const prevBusinessTypeId = usePrevious(businessTypeId)
  const userValue = getUserValue(accId, user, name)

  useEffect(() => {
    if (businessTypeId) {
      fetchEmploymentPositions(businessTypeId).then(
        /**
         * @param {Object[]} list
         */
        (list) => {
          setFullList(list)
          const formattedList = map(({ value, id }) => ({ value: id, label: value }), list)
          setList(formattedList)
        }, (error) => setFetchError(error.message))
    } else {
      setList([])
    }
    if (prevBusinessTypeId && prevBusinessTypeId !== businessTypeId) {
      updateValue('')
    }
  }, [businessTypeId, i18n.language])

  useEffect(() => {
    if (employmentSituationId === EMPLOYMENT_ID.UNEMPLOYED || employmentSituationId === EMPLOYMENT_ID.RETIRED) {
      setFldLabel(t(`generatedField.previous${label}`))
    } else {
      setFldLabel(t(`generatedField.${label}`))
    }
  }, [employmentSituationId, i18n.language])

  useEffect(() => {
    if (!isNil(userValue) && userValue !== '') {
      if (!isValuePreset) {
        updateValue(userValue.toString())
        setIsValuePreset(true)
      }
      setDetailsRequired(userValue.toString())
    }
  }, [userValue, name, fullList])

  /**
   * @param {string} value
   */
  const getSelectValue = (value) => (equals(value, t('generatedField.choose')) ? '' : value)

  /**
   * @param {string} value
   */
  const updateValue = (value) => {
    setValue(value)
    setIsValid(value.length > 0)
    dispatch({ type: 'setExtFieldValue', name, value: +value })
    dispatch({ type: 'setValidationFld', name, value: value.length > 0 })
  }

  /**
   * @param {string} value
   */
  const setDetailsRequired = (value) => {
    const item = fullList.find(item => item.id === +value)
    if (item) {
      dispatch({
        type: 'setExtFieldValue',
        value: item.detailsRequired,
        name: 'employmentDetailsPositionIsShown'
      })
    } else {
      dispatch({
        type: 'setExtFieldValue',
        value: false,
        name: 'employmentDetailsPositionIsShown'
      })
    }
  }

  const onChange = ({ target: { value } }) => {
    const selectedValue = getSelectValue(value)
    if (selectedValue !== t('generatedField.choose')) {
      updateValue(selectedValue)
    } else {
      updateValue('')
    }
    setDetailsRequired(value)
  }

  return (
    <>
      <Select
        value={value || ''}
        label={fldLabel}
        placeholder={t('generatedField.choose')}
        options={list}
        onChange={onChange}
        name={name}
        key={name}
        error=''
        valid={isValid}
        info={additionalInfo ? t(`generatedField.${name}AdditionalInfo`) : ''}
        showLabelAsPlaceholder={showLabelAsPlaceholder}
      />
      {fetchError}
    </>
  )
}

const getExternalEmploymentPositionFields = (positionField) => {
  const { i18n, name, required, order } = positionField
  return (
    <ExternalEmploymentPosition
      label={i18n}
      name={name}
      required={required}
      key={name}
      order={order}
    />
  )
}

export { ExternalEmploymentPosition, getExternalEmploymentPositionFields }
